import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { CalculationResults, CalculationState, CalculationStatus } from "@/types/calculation.types";
import { Formulation } from "@/types/formulation.types";

export type RequestErrorState = {
    status: number | undefined;
    errorMessages?: string[];
};

export type CalculationMetadata = {
    submittedFormulation: Formulation | null;
};

export type CalculationLastSucceededFormulationState = {
    formulation: Formulation;
    results: CalculationResults;
};

export type CalculationStoreState = {
    id: string | null;
    status: CalculationStatus | null;
    requestError: RequestErrorState | null;
    metadata: CalculationMetadata;
    lastSucceededFormulation: CalculationLastSucceededFormulationState | null;
};

const INITIAL_STATE: CalculationStoreState = {
    id: null,
    status: null,
    requestError: null,
    metadata: {
        submittedFormulation: null,
    },
    lastSucceededFormulation: null,
};

const calculationSlice = createSlice({
    name: "calculation",
    initialState: INITIAL_STATE,
    reducers: {
        setCalculationId: (state, action: PayloadAction<string | null>) => {
            state.id = action.payload;
        },
        setCalculationRequestError: (state, action: PayloadAction<RequestErrorState>) => {
            state.requestError = action.payload;
        },
        setCalculationStatus: (state, action: PayloadAction<CalculationStatus | null>) => {
            state.status = action.payload;
            if (action.payload?.state === CalculationState.Succeeded && state.metadata.submittedFormulation !== null) {
                state.lastSucceededFormulation = {
                    formulation: state.metadata.submittedFormulation,
                    results: action.payload.results,
                };
            }
        },
        setCalculationMetadata: (state, action: PayloadAction<Partial<CalculationMetadata>>) => {
            state.metadata = { ...state.metadata, ...action.payload };
        },
        resetCalculation: (state) => ({
            ...INITIAL_STATE,
            lastSucceededFormulation: state.lastSucceededFormulation,
        }),
    },
});

export const {
    setCalculationId,
    setCalculationRequestError,
    setCalculationStatus,
    setCalculationMetadata,
    resetCalculation,
} = calculationSlice.actions;
export const calculationReducer = calculationSlice.reducer;
